import { CameraAlt, Delete, FileDownload } from "@mui/icons-material";
import { IconButton, Stack, Tooltip } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { GqlFilterOption, GqlProjectExport, GqlSettingsResearchType } from "~/__generated__";
import GaiaDataList, { GaiaDataListProps } from "~/modules/app/components/GaiaDataList";
import { parseStringToDate } from "~/modules/app/util/dateHelpers";
import { formatDateTime } from "~/modules/app/util/formatters";

import { ExportStatus } from "./ExportStatus";

type ProjectExportProps = Pick<
    GqlProjectExport,
    "id" | "dataJobId" | "mediaIncluded" | "mediaJobId" | "createdAt" | "template"
> & {
    researchTypes?: Pick<GqlSettingsResearchType, "id" | "name">[] | null;
    dataMediaItem?: Pick<NonNullable<GqlProjectExport["dataMediaItem"]>, "url"> | null;
    mediaMediaItem?: Pick<NonNullable<GqlProjectExport["mediaMediaItem"]>, "url"> | null;
    projects?: Pick<NonNullable<GqlProjectExport["projects"]>[number], "id" | "name">[] | null;
};

interface ProjectExportTableProps {
    data: ProjectExportProps[];
    error?: Error;
    filterModel: GaiaDataListProps<any>["filterModel"];
    filterOptions: GqlFilterOption[];
    gridModels: GaiaDataListProps<any>["gridModels"];
    loading: boolean;
    onDeleteClick?: (id: string) => void;
    onFilterModelChange: GaiaDataListProps<any>["onFilterModelChange"];
    onRefreshRequest: () => void;
    total: number;
}

export function ProjectExportTable({
    data,
    error,
    filterModel,
    filterOptions,
    gridModels,
    loading,
    onDeleteClick,
    onFilterModelChange,
    onRefreshRequest,
    total,
}: ProjectExportTableProps) {
    const { t } = useTranslation("projectExport");

    const columns: GridColDef<(typeof data)[number]>[] = useMemo(
        () => [
            {
                field: "createdAt",
                headerName: t("ProjectExportListScreen.createdAt"),
                flex: 1,
                valueGetter: (_, row) => formatDateTime(parseStringToDate(row.createdAt)),
            },
            {
                field: "speciesGroups",
                headerName: t("ProjectExportListScreen.researchTypes"),
                valueGetter: (_, row) => (row.researchTypes ?? []).map(x => x.name).join(", "),
                flex: 1,
            },
            {
                field: "projects",
                headerName: t("ProjectExportListScreen.projectIds"),
                valueGetter: (_, row) => row.projects?.length ?? 0,
                flex: 1,
            },
            {
                field: "template",
                headerName: t("ProjectExportListScreen.template"),
                flex: 1,
            },
            {
                field: "fileExportStatus",
                headerName: t("ProjectExportListScreen.status"),
                renderCell: params => {
                    const isMediaPending = params.row.mediaIncluded && params.row.mediaJobId && !params.row.mediaMediaItem;
                    const canDelete = onDeleteClick !== undefined && params.row.dataMediaItem !== null && !isMediaPending;

                    return (
                        <Stack direction="row" alignItems="center" height="100%" columnGap={1}>
                            {params.row.dataJobId && (
                                <ExportStatus
                                    icon={<FileDownload />}
                                    jobId={params.row.dataJobId}
                                    url={params.row.dataMediaItem?.url as string | undefined}
                                    onFinished={onRefreshRequest}
                                    worker="ProjectDataExportWorker"
                                    label={t("ExportStatus.dataFiles")}
                                />
                            )}

                            {params.row.mediaIncluded && params.row.mediaJobId && (
                                <ExportStatus
                                    icon={<CameraAlt />}
                                    jobId={params.row.mediaJobId}
                                    url={params.row.mediaMediaItem?.url as string | undefined}
                                    onFinished={onRefreshRequest}
                                    worker="ProjectMediaExportWorker"
                                    label={t("ExportStatus.mediaFiles")}
                                />
                            )}

                            {canDelete && (
                                <Tooltip title={t("ProjectExportTable.delete")}>
                                    <IconButton onClick={() => onDeleteClick(params.row.id)}>
                                        <Delete color="error" />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Stack>
                    );
                },
                flex: 1,
            },
        ],
        [onDeleteClick, onRefreshRequest, t],
    );

    return (
        <GaiaDataList
            data={data}
            loading={loading}
            error={error}
            filterOptions={filterOptions}
            totalRows={total}
            columns={columns}
            filterModel={filterModel}
            onFilterModelChange={onFilterModelChange}
            gridModels={gridModels}
        />
    );
}
